import { IIndicatorFilterResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

const defaultIndicatorFilterSort: DataSourceSort[] = [{ field: 'SortOrder', dir: 'asc' }];

export type IndicatorFilterFilter = Readonly<{
    nameSearch?: string;
    locationId?: string;
    isGlobal?: boolean;
}>;

export type UseIndicatorFilterDataSourceOptions = BaseDataSourceOptions<
    IIndicatorFilterResponse,
    IndicatorFilterFilter
>;

export type InfiniteIndicatorFiltersDataSource = InfiniteDataSource<
    IIndicatorFilterResponse,
    IndicatorFilterFilter
>;

const getFilter = ({ locationId, isGlobal }: IndicatorFilterFilter) => {
    const locationFilter = locationId ? Filter.eq('LocationId', locationId) : undefined;
    if (isGlobal) {
        return Filter.isNull('Location.LocationName');
    }

    // globals are implicitly filtered by location filter unless includeGlobal is true,
    // so only deliberately filter them if there is no location filter
    if (isGlobal === false && locationFilter === undefined) {
        return Filter.eq('IsGlobal', false);
    }
    return locationFilter;
};

const queryFn: DataSourceQuery<IIndicatorFilterResponse, IndicatorFilterFilter> = ({
    filter: { nameSearch, ...filterRest },
    sort,
    page,
    pageLimit,
}) => {
    const locationExp = getFilter(filterRest);
    const globalExp = getFilter({ isGlobal: true });
    const combinedFilterExp = Filter.or(globalExp, locationExp);
    const orderBy = buildOrderBy(sort);
    const indicatorFilters = toDataSourceResult(
        sdClient.getIndicatorFilters({
            filter: combinedFilterExp,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        }),
    );

    return indicatorFilters;
};

export function useInfiniteIndicatorFiltersDataSource({
    defaultSort = defaultIndicatorFilterSort,
    ...options
}: UseIndicatorFilterDataSourceOptions): InfiniteIndicatorFiltersDataSource {
    const locationIndicatorsFilters = useInfiniteDataSource({
        defaultSort,
        ...options,
        query: queryFn,
    });

    return locationIndicatorsFilters;
}
